import { AxiosInstance, AxiosRequestConfig } from "axios";
import { TrainingCourseField } from "../../../../enums";
import { IFlightPathApiResponse } from "../BaseApiModel";
import { ExportableProjectApi } from "../exportableApi/ExportableProjectApiModel";
import http from "../Http";
import { IFilteredResponse } from "../filteredApi/FilteredApiModel";

export class TrainingCoursesApi extends ExportableProjectApi<FP.Entities.ITrainingCourse> {
  controller: string = "training-courses";

  constructor(http: AxiosInstance) {
    super(http, "training-courses");
  }

  deleteRange = async (organisationId: number, projectId: number, ids: number[], config?: AxiosRequestConfig) => {
    let res = await this.http.post(
      `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/delete-range`,
      ids,
      config
    );
    return res.data;
  };

  getDetailedById = async (
    organisationId: number,
    projectId: number,
    trainingCourseId: number
  ): Promise<IFlightPathApiResponse<FP.Entities.ITrainingCourse>> => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseId}/detailed`;
    let res = await this.http.get(url);
    return res.data;
  };

  deleteField = async (
    organisationId: number,
    projectId: number,
    trainingCourseIds: number,
    field: TrainingCourseField,
    config?: AxiosRequestConfig
  ): Promise<IFlightPathApiResponse<boolean>> => {
    let res = await this.http.delete(
      `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseIds}/${field}`,
      config
    );
    return res.data;
  };

  getGridData = async (organisationId: number, projectId: number, config?: AxiosRequestConfig) => {
    const res = await this.http.get(
      `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/grid-data`,
      config
    );
    return res.data;
  };

  getDetailedByIdAsync = async (organisationId: number, projectId: number, id: number, config?: AxiosRequestConfig) => {
    const res = await this.http.get(
      `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${id}`,
      config
    );
    return res.data;
  };

  updateField = async (
    organisationId: number,
    projectId: number,
    trainingCourseIds: number,
    textField: string,
    idField: number,
    dateField: Date,
    numericField: number,
    field: TrainingCourseField,
    config?: AxiosRequestConfig
  ): Promise<IFlightPathApiResponse<FP.Entities.ITrainingCourse>> => {
    let data = {
      TextField: textField,
      IdField: idField,
      DateField: dateField,
      NumericField: numericField
    };
    let res = await this.http.put(
      `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseIds}/${field}`,
      data,
      config
    );
    return res.data;
  };

  getByProjectIdAndName = async (
    organisationId: number,
    projectId: number,
    name: string,
    config?: AxiosRequestConfig
  ): Promise<IFilteredResponse<any>> => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/name/${name}`;
    let res = await this.http.get(url, config);
    if (res && res.status === 200) {
      return res.data;
    }
  };

  addBulkActionsToTrainingCourses = async (
    organisationId: number,
    projectId: number,
    trainingCourseIds: number[],
    actionIds: number[],
    config?: AxiosRequestConfig
  ) => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/add-bulk-actions-to-training-courses`;
    let res = await this.http.post(url, { actionIds, trainingCourseIds }, config);
    return res.data;
  };

  addBulkProjectProcessesToTrainingCourses = async (
    organisationId: number,
    projectId: number,
    trainingCourseIds: number[],
    projectProcessIds: number[],
    config?: AxiosRequestConfig
  ) => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/add-bulk-processes-to-training-courses`;
    let res = await this.http.post(url, { projectProcessIds, trainingCourseIds }, config);
    return res.data;
  };

  addBulkProjectStakeholdersToTrainingCourses = async (
    organisationId: number,
    projectId: number,
    trainingCourseIds: number[],
    projectStakeholderIds: number[],
    config?: AxiosRequestConfig
  ) => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/add-bulk-project-stakeholders-to-training-courses`;
    let res = await this.http.post(url, { projectStakeholderIds, trainingCourseIds }, config);
    return res.data;
  };

  getTrainingCourseActionsByTrainingCourseId = async (
    organisationId: number,
    projectId: number,
    trainingCourseId: number,
    config?: AxiosRequestConfig
  ) => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseId}/actionIds`;
    let res = await this.http.get(url, config);
    return res.data;
  };

  getTrainingCourseProcessesByTrainingCourseId = async (
    organisationId: number,
    projectId: number,
    trainingCourseId: number,
    config?: AxiosRequestConfig
  ) => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseId}/projectProcessIds`;
    let res = await this.http.get(url, config);
    return res.data;
  };

  getTrainingCourseProjectStakeholdersByTrainingCourseId = async (
    organisationId: number,
    projectId: number,
    trainingCourseId: number,
    config?: AxiosRequestConfig
  ) => {
    const url = `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseId}/projectStakeholderIds`;
    let res = await this.http.get(url, config);
    return res.data;
  };

  removeTrainingCourseStakeholderOwners = async (
    organisationId: number,
    projectId: number,
    trainingCourseId: number,
    projectStakeholderIds: number[],
    config?: AxiosRequestConfig
  ) => {
    const res = await this.http.delete(
      `${this.url}/${organisationId}/projects/${projectId}/${this.controller}/${trainingCourseId}/project-stakeholder-owners/${projectStakeholderIds}`,
      config
    );
    return res.data;
  };
}

const instance = new TrainingCoursesApi(http);
export default instance;
